// @system — Pure helpers for the article page: heading extraction for the "On this page" list,
// heading-id fallback and splitting the HTML so tables / code blocks can be wrapped in ScrollRail.
// String-based on purpose (no DOM) so they behave identically under SSR and in the browser.

/** @typedef {{ id: string, text: string }} ArticleHeading */

/**
 * URL-safe anchor from heading text — the same rule the server's renderer uses for heading ids,
 * so a missing id on the wire resolves to the same anchor the TOC links to.
 * @param {string} str
 */
export function slugifyHeading(str) {
  return str.toLowerCase().replace(/<[^>]+>/g, '').replace(/&[a-z#0-9]+;/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

/** @param {string} html */
function stripTags(html) {
  return html.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
}

/**
 * Give every `<h2>` an id (kept when the server already set one).
 * @param {string} html
 */
export function ensureHeadingIds(html) {
  return html.replace(/<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi, (match, attrs = '', inner) => {
    if (/\sid\s*=/.test(attrs)) return match
    const id = slugifyHeading(stripTags(inner))
    return id ? `<h2${attrs} id="${id}">${inner}</h2>` : match
  })
}

/**
 * The h2 headings of `content_html`, in order — the article's "On this page" list.
 * @param {string | null | undefined} html
 * @returns {ArticleHeading[]}
 */
export function extractHeadings(html) {
  if (!html) return []
  /** @type {ArticleHeading[]} */
  const out = []
  const re = /<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi
  let m
  while ((m = re.exec(html)) !== null) {
    const attrs = m[1] ?? ''
    const text = stripTags(m[2])
    if (!text) continue
    const idMatch = /\sid\s*=\s*["']([^"']+)["']/.exec(attrs)
    const id = idMatch ? idMatch[1] : slugifyHeading(text)
    if (id) out.push({ id, text })
  }
  return out
}

/** @typedef {{ kind: 'html' | 'rail', html: string }} ArticleSegment */

/**
 * Split the article into plain segments and "rail" segments (`<table>`/`<pre>` blocks) so the
 * page can render the latter inside ScrollRail — wide tables and code scroll sideways with a
 * keyboard-reachable rail instead of widening the page (docs/ACCESSIBILITY.md §3).
 * @param {string} html
 * @returns {ArticleSegment[]}
 */
export function splitArticle(html) {
  /** @type {ArticleSegment[]} */
  const segments = []
  const re = /<(table|pre)(\s[^>]*)?>[\s\S]*?<\/\1>/gi
  let last = 0
  let m
  while ((m = re.exec(html)) !== null) {
    if (m.index > last) segments.push({ kind: 'html', html: html.slice(last, m.index) })
    segments.push({ kind: 'rail', html: m[0] })
    last = m.index + m[0].length
  }
  if (last < html.length) segments.push({ kind: 'html', html: html.slice(last) })
  return segments.filter((s) => s.html.trim().length > 0)
}
